import {Injectable, OnDestroy} from '@angular/core';
import {Subject} from 'rxjs';
import {environment} from '@env/environment';

@Injectable()
export class UserActivityService implements OnDestroy {

    public onUserInactive$: Subject<any> = new Subject();
    public promptUserActivityConfirmation$: Subject<boolean> = new Subject();

    private sessionLastActivity: number;
    private inactivityTimeoutId;
    private confirmationTimeoutId;

    private readonly CONFIRMATION_TIMEOUT_MS = (1 * 60 * 1000); // 1 minute by default
    private readonly INACTIVITY_TIMEOUT_MS = (environment.customer.userInactivityTimeout || 30 * 60 * 1000) - this.CONFIRMATION_TIMEOUT_MS; // 30 minutes by default
    private readonly UPDATE_THRESHOLD = (10 * 1000); // 10sec

    constructor() {
        this.onStorageEvent = this.onStorageEvent.bind(this);
    }

    public ngOnDestroy(): void {
        this.clearAllActivityTimers();
        this.stopActivitySyncAcrossTabs();
        this.onUserInactive$.complete();
        this.promptUserActivityConfirmation$.complete();
    }

    public startActivitySyncAcrossTabs() {
        window.addEventListener('storage', this.onStorageEvent);
    }

    public stopActivitySyncAcrossTabs() {
        window.removeEventListener('storage', this.onStorageEvent);
    }

    public refreshInactivityTimeout(sessionLastActivity: number = Date.now()) {
        if (!this.sessionLastActivity || (sessionLastActivity > (this.sessionLastActivity + this.UPDATE_THRESHOLD))) {
            this.sessionLastActivity = sessionLastActivity;
            this.clearAllActivityTimers();
            this.initializeInactivityTracking();
        }
    }

    private onStorageEvent(event: StorageEvent) {
        if ((event.key === 'sessionLastActivity') && event.newValue) {
            const sessionLastActivity = Number(event.newValue);
            if (sessionLastActivity !== this.sessionLastActivity) {
                this.refreshInactivityTimeout(sessionLastActivity);
            }
        }
    }

    private initializeInactivityTracking() {
        window.localStorage.setItem('sessionLastActivity', this.sessionLastActivity.toString());
        this.startInactivityTimer();
    }

    private startInactivityTimer(timeout = this.INACTIVITY_TIMEOUT_MS) {
        this.promptUserActivityConfirmation$.next(false);
        this.inactivityTimeoutId = setTimeout( () =>  {
            this.checkInactivityStatus();
        }, timeout);
    }

    private checkInactivityStatus() {
        const sessionLastActivity = Number(window.localStorage.getItem('sessionLastActivity'));
        if (sessionLastActivity) {
            const now = Date.now();
            this.clearAllActivityTimers();

            if (now <= (sessionLastActivity + this.INACTIVITY_TIMEOUT_MS)) {
                this.startInactivityTimer(sessionLastActivity + this.INACTIVITY_TIMEOUT_MS - now);
            } else if ((now > (sessionLastActivity + this.INACTIVITY_TIMEOUT_MS)) &&
                (now < (sessionLastActivity + this.INACTIVITY_TIMEOUT_MS + this.CONFIRMATION_TIMEOUT_MS))
            ) {
                this.startConfirmationTimer(sessionLastActivity + this.INACTIVITY_TIMEOUT_MS + this.CONFIRMATION_TIMEOUT_MS - now);
            } else {
                this.handleUserInactivity();
            }
        }
    }

    private startConfirmationTimer(timeout = this.CONFIRMATION_TIMEOUT_MS) {
        this.promptUserActivityConfirmation$.next(true);
        this.confirmationTimeoutId = setTimeout(() => {
            this.checkInactivityStatus();
        }, timeout);
    }

    private handleUserInactivity() {
        this.clearAllActivityTimers();
        this.stopActivitySyncAcrossTabs();
        this.onUserInactive$.next(undefined);
    }

    private clearAllActivityTimers() {
        clearTimeout(this.inactivityTimeoutId);
        clearTimeout(this.confirmationTimeoutId);
        this.inactivityTimeoutId = undefined;
        this.confirmationTimeoutId = undefined;
    }
}
